import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import type { AdminTicketActivityDto, AppTicketDto, TicketCommentDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Lock01Icon } from "@/components/Icons/Lock01Icon";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { TicketActivitySection } from "@/components/Ticket/TicketActivitySection";
import { TicketContent } from "@/components/Ticket/TicketDetails/TicketInfoHeader";
import { TicketResidentAppointmentCard } from "@/components/Ticket/TicketDetails/TicketResidentAppointmentCard";
import type { ResidentCreateCommentPayload } from "@/components/Ticket/TicketResidentCommentField";
import { TicketResidentCommentField } from "@/components/Ticket/TicketResidentCommentField";
import { TicketResidentRateModal } from "@/components/Ticket/TicketResidentRateModal";
import { TicketResidentRejectModal } from "@/components/Ticket/TicketResidentRejectModal";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { RatingBadge } from "@/design-system/RatingBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { daysBetween } from "@/helpers/date";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { ActivityFeed, ActivityFeedFooter, ActivityFeedPrompt } from "@/product-patterns/tickets/ActivityFeed";
import { DetailField, DetailFieldGroup, DetailFieldInline } from "@/product-patterns/tickets/DetailField";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { getVisibilityOptionIcon } from "../list/helpers";

export interface ResidentTicketDetailsContentProps {
  ticket: AppTicketDto;
  comments: TicketCommentDto[];
  isFetchingMoreComments: boolean;
  hasMoreComments: boolean;
  fetchMoreComments: () => unknown;
  onCreateComment: (payload: ResidentCreateCommentPayload) => Promise<void>;
}
export function ResidentTicketDetailsContent({
  ticket,
  comments,
  isFetchingMoreComments,
  hasMoreComments,
  fetchMoreComments,
  onCreateComment,
}: ResidentTicketDetailsContentProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { i18n, t } = useTranslation();
  const [isRejectModalOpened, rejectModalOpenHandlers] = useBool(false);
  const [isRateModalOpened, rateModalOpenHandlers] = useBool(false);
  const canRateTicket = ticket.status.type !== "rated" && ticket.author.id === sessionUser.id;
  const ticketQueries = useTicketQueries();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (canRateTicket) {
      void queryClient.prefetchQuery(ticketQueries.ratingFeedbackOptions());
    }
  }, [canRateTicket, queryClient, ticketQueries]);
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreComments && hasMoreComments) {
        fetchMoreComments();
      }
    }, [fetchMoreComments, hasMoreComments, isFetchingMoreComments]),
  });
  const activities = useMemo(() => {
    return comments.map(
      (comment) =>
        ({
          id: comment.id,
          postedAt: comment.postedAt,
          author: comment.author,
          comment: {
            ...comment,
            internal: false,
            images: comment.image ? [comment.image] : [],
            canEdit: false,
            canDelete: false,
          },
        }) satisfies AdminTicketActivityDto,
    );
  }, [comments]);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={ticket.title.trim() || t("page.tickets.details.title")}
      contentSize="constrained"
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.tickets.title"),
              to: routes.tickets.list({
                slug,
              }),
            },
            {
              name: t("page.tickets.details.title"),
            },
          ]}
        />
      }
      actions={
        canRateTicket ? (
          <Button
            variant="soft"
            icon={<ThumbsUpIcon />}
            onClick={rateModalOpenHandlers.setTrue}
            data-testid="rate-close-ticket"
          >
            {t("page.tickets.details.actions.rate-or-close")}
          </Button>
        ) : null
      }
    >
      <Stack gap="md">
        <SectionCard>
          {ticket.closedAt && (ticket.status.type === "rated" || ticket.status.type === "autoClosed") && (
            <Notice
              icon={<Lock01Icon />}
              variant="special"
              message={t("page.tickets.details.info.closed-at", {
                date: formatDate(i18n, "date", ticket.closedAt),
              })}
            />
          )}
          <DetailFieldGroup>
            {ticket.visibility !== "private" && (
              <DetailField label={t("page.tickets.details.info.type")}>
                <Badge icon={getVisibilityOptionIcon(ticket.visibility)} variant="default" size="md">
                  {t("page.tickets.details.info.type.collective")}
                </Badge>
              </DetailField>
            )}
            <DetailField label={t("page.tickets.details.info.status")}>
              <DetailFieldInline>
                <TicketStatus description={ticket.status.name} labelColor={ticket.status.color} variant="dot" />
              </DetailFieldInline>
            </DetailField>
            <DetailField label={t("page.tickets.details.info.category")} value={ticket.category.name} />{" "}
            <DetailField
              label={t("page.tickets.details.info.open-since")}
              value={t("page.tickets.details.info.open-since.content", {
                count: daysBetween(ticket.postedAt, clockDate),
              })}
            />
            {!!ticket.rating && (
              <DetailField label={t("page.tickets.details.info.rating")}>
                <RatingBadge rating={ticket.rating} data-testid="rating-number" />
              </DetailField>
            )}
          </DetailFieldGroup>
          <TicketContent ticket={ticket} />
          {ticket.appointments?.hasDetails && <TicketResidentAppointmentCard ticketId={ticket.id} />}
        </SectionCard>

        <SectionCard title={t("page.tickets.details.activities.title")}>
          <Stack gap="sm">
            <Stack>
              <Stack>
                {ticket.author.id !== sessionUser.id && ticket.status.type !== "rated" && (
                  <Text as="div" variant="caption" tone="secondary" align="center">
                    {t("page.tickets.details.comment-field.resident.comment-not-allowed")}
                  </Text>
                )}

                {ticket.status.type === "closed" && canRateTicket && (
                  <ActivityFeedPrompt
                    message={
                      <Notice
                        icon={<Star01Icon />}
                        variant="warning"
                        message={t("page.tickets.details.comment-field.resident.ticket-settled", {
                          status: ticket.status.name || "",
                        })}
                      />
                    }
                    actions={
                      <Inline gap="sm" wrap={false}>
                        {ticket.canReject && (
                          <Button
                            data-testid="reject-ticket-btn"
                            variant="soft"
                            onClick={rejectModalOpenHandlers.setTrue}
                          >
                            {t("page.tickets.details.comment-field.resident.reject")}
                          </Button>
                        )}
                        {canRateTicket && (
                          <Button
                            data-testid="rate-ticket-btn"
                            variant="filled"
                            onClick={rateModalOpenHandlers.setTrue}
                          >
                            {t("page.tickets.details.comment-field.resident.rate")}
                          </Button>
                        )}
                      </Inline>
                    }
                  />
                )}
                {ticket.canComment && !canRateTicket && !ticket.canReject && ticket.requestOrigin === "web" && (
                  <Box padding={{ top: "sm" }}>
                    <Text variant="overline" tone="secondary">
                      {t("page.tickets.details.comment-field.resident.notification-reminder")}
                    </Text>
                  </Box>
                )}
              </Stack>
              <ActivityFeed presentation="soft">
                <TicketActivitySection
                  ticket={ticket}
                  isForResident
                  activities={
                    !ticket.rating && !ticket.remark
                      ? activities
                      : [
                          {
                            author: ticket.author,
                            id: "rating",
                            postedAt: ticket.closedAt || clockDate.toISOString(),
                            rating: {
                              rating: ticket.rating,
                              ratingRemark: ticket.remark,
                              ratingFeedback: ticket.ratingFeedbacks,
                            },
                          },
                          ...activities,
                        ]
                  }
                />
                {hasMoreComments ? (
                  <ActivityFeedFooter isLoading ref={ref}>
                    <Spinner size="md" />
                  </ActivityFeedFooter>
                ) : null}
              </ActivityFeed>
              {/* We hide comment field and show only rejection button if the ticket can be rejected*/}
              {ticket.canComment && !ticket.canReject && (
                <TicketResidentCommentField onComment={onCreateComment} user={sessionUser} />
              )}
            </Stack>
          </Stack>
        </SectionCard>
      </Stack>
      <TicketResidentRejectModal
        isOpened={isRejectModalOpened}
        onOpenChange={rejectModalOpenHandlers.set}
        onSubmit={async (payload) => {
          try {
            await onCreateComment(payload);
          } finally {
            rejectModalOpenHandlers.setFalse();
          }
        }}
      />
      <TicketResidentRateModal isOpened={isRateModalOpened} onOpenChange={rateModalOpenHandlers.set} ticket={ticket} />
    </DocumentPage>
  );
}
